import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  Tags,
  Plus,
  Trash2,
  Hash,
  X,
  Sparkles,
  Save,
  CheckCircle2,
  LayoutTemplate,
  Star,
  Globe,
  Upload,
  Loader2,
  Send,
  RefreshCw,
  Eye,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { TOPIC_COLORS, type Topic } from '../../data/subscriberMockData';
import {
  LANGUAGE_OPTIONS,
  LanguageFlagByCode,
  type LanguageCode,
} from '../../components/LanguageFlag';
import { subscribersApi } from '../../../api/subscribersApi';
import { topicsApi } from '../../../api/topicsApi';
import { buzzwordsApi } from '../../../api/buzzwordsApi';
import { subscriberScheduleApi } from '../../../api/subscriberScheduleApi';
import { emailTemplatesApi } from '../../../api/emailTemplatesApi';
import { emailNewsletterApi } from '../../../api/emailNewsletterApi';
import { aiGenerationApi } from '../../../api/aiGenerationApi';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

const LIQUID_GLASS_PANEL =
  'border border-white/55 bg-white/35 shadow-[0_18px_60px_-32px_rgba(30,64,175,0.45)] ring-1 ring-white/35 backdrop-blur-2xl backdrop-saturate-150';
const LIQUID_GLASS_CARD =
  'border border-white/55 bg-white/50 shadow-[0_16px_44px_-30px_rgba(15,23,42,0.42)] ring-1 ring-white/45 backdrop-blur-xl backdrop-saturate-150';
const LIQUID_GLASS_SOFT =
  'border border-white/45 bg-white/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] backdrop-blur-md';
const GLASS_FIELD =
  'rounded-xl border-white/55 bg-white/45 text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] ring-1 ring-white/40 backdrop-blur-md transition-colors hover:bg-white/55 focus-visible:border-white/70 focus-visible:ring-blue-200/50';

const LANGUAGE_CODES = Object.keys(LANGUAGE_OPTIONS) as LanguageCode[];

function normalizeLanguageCode(value: unknown): LanguageCode {
  const raw = String(value ?? 'en').toLowerCase();
  if (LANGUAGE_CODES.includes(raw as LanguageCode)) return raw as LanguageCode;
  return 'en';
}

function languageDigestLabel(code: LanguageCode): string {
  return LANGUAGE_OPTIONS[code].countryLabel.split(' ').slice(1).join(' ');
}

function formatSendTimeDisplay(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return time24;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}

type ToastApi = {
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
};

function scheduleApiError(error: unknown): string {
  const ax = error as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((x: { msg?: string }) => x?.msg).filter(Boolean).join(', ') || 'Request failed';
  }
  return ax.message || 'Request failed';
}

/** Map API topic shape → local Topic shape */

/**
 * The DB column is VARCHAR(20). Store only a short token (e.g. "blue") and
 * reconstruct the full Tailwind class string in the frontend.
 */
const COLOR_TOKEN_MAP: Record<string, string> = {
  blue:   'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  green:  'bg-green-100 text-green-700 border-green-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  rose:   'bg-rose-100 text-rose-700 border-rose-200',
  cyan:   'bg-cyan-100 text-cyan-700 border-cyan-200',
};
const COLOR_TOKENS = Object.keys(COLOR_TOKEN_MAP);

/** Full Tailwind class → short token (for storage). Unknown classes fall back to index-based token. */
function colorToToken(fullClass: string, fallbackIdx: number): string {
  const found = COLOR_TOKENS.find((t) => fullClass.includes(t));
  return found ?? COLOR_TOKENS[fallbackIdx % COLOR_TOKENS.length];
}

/** Short token (or legacy full class) → full Tailwind class (for display). */
function tokenToColor(token: string | null | undefined, fallbackIdx: number): string {
  if (!token) return COLOR_TOKEN_MAP[COLOR_TOKENS[fallbackIdx % COLOR_TOKENS.length]];
  // Already a full class (legacy data in DB)
  if (token.includes('bg-')) return token;
  return COLOR_TOKEN_MAP[token] ?? COLOR_TOKEN_MAP[COLOR_TOKENS[fallbackIdx % COLOR_TOKENS.length]];
}

function apiTopicToLocal(t: any, idx: number): Topic {
  return {
    id: t.id,
    name: t.topic ?? t.name ?? '',
    buzzwords: (t.buzzwords ?? []).map((b: any) => b.buzzword ?? b),
    color: tokenToColor(t.color, idx),
  };
}

/** Build a buzzword→id lookup from raw API topic */
function buildBuzzwordIdMap(apiTopics: any[]): Record<number, Record<string, number>> {
  const map: Record<number, Record<string, number>> = {};
  for (const t of apiTopics) {
    map[t.id] = {};
    for (const b of (t.buzzwords ?? [])) {
      map[t.id][b.buzzword ?? b] = b.id ?? -1;
    }
  }
  return map;
}

export function SubscriberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useUiLocale();
  const { showSuccess, showError } = useToast() as ToastApi;

  const subscriberId = Number(id);

  /* ── Live subscriber data (loaded from API) ── */
  const [subscriber, setSubscriber] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  /* ── Subscriber settings state ── */
  const [language, setLanguage] = useState<LanguageCode>('en');
  const [sendTime, setSendTime] = useState('09:00');
  /** IANA timezone for daily auto-send (matches users.timezone) */
  const [sendTimezone, setSendTimezone] = useState('Europe/Berlin');
  const [templateId, setTemplateId] = useState<number | null>(null);
  /**
   * Settings save lifecycle:
   *   idle  → no edits since load/last reset; header shows nothing
   *   dirty → a settings field changed; header shows the "Save changes" button
   *   saved → save just succeeded; header shows the badge for 1.5s, then → idle
   */
  const [saveState, setSaveState] = useState<'idle' | 'dirty' | 'saved'>('idle');
  const [savingSettings, setSavingSettings] = useState(false);
  /** Timer that fades the "saved" badge back to idle. */
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** An edit landed while the "saved" badge was showing — flip to dirty once it finishes. */
  const pendingDirtyRef = useRef(false);

  /** Mark settings dirty, but never interrupt a playing "saved" badge (UX decision). */
  const markDirty = useCallback(() => {
    setSaveState((prev) => {
      if (prev === 'saved') {
        pendingDirtyRef.current = true;
        return prev;
      }
      return 'dirty';
    });
  }, []);

  // Clear the badge timer if the page unmounts mid-window.
  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);
  const [sendingNewsletterNow, setSendingNewsletterNow] = useState(false);

  /* ── Topics state ── */
  const [topics, setTopics] = useState<Topic[]>([]);
  const [nextTopicId, setNextTopicId] = useState(200);
  const [newTopicName, setNewTopicName] = useState('');
  const [newBuzzwords, setNewBuzzwords] = useState<Record<number, string>>({});
  /** buzzword text → API id, keyed by topic id */
  const [buzzwordIdMap, setBuzzwordIdMap] = useState<Record<number, Record<string, number>>>({});

  /* ── Templates list ── */
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);

  /* ── Initial data load ── */
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);

    Promise.allSettled([
      subscribersApi.get(subscriberId),
      topicsApi.list(subscriberId, { signal: ctrl.signal }),
      subscriberScheduleApi.get(subscriberId, { signal: ctrl.signal }),
      emailTemplatesApi.list({ signal: ctrl.signal }),
    ]).then(([subRes, topicsRes, scheduleRes, templatesRes]) => {
      if (subRes.status === 'fulfilled') {
        const s = subRes.value.data as any;
        setSubscriber(s);
        setLanguage(normalizeLanguageCode(s.language));
        const rawTpl = s.preferred_template_id ?? s.template_id;
        setTemplateId(
          rawTpl !== null && rawTpl !== undefined && rawTpl !== ''
            ? Number(rawTpl)
            : null,
        );
      }
      if (topicsRes.status === 'fulfilled') {
        const apiTopics = topicsRes.value.data as any[];
        setTopics(apiTopics.map((t, i) => apiTopicToLocal(t, i)));
        setBuzzwordIdMap(buildBuzzwordIdMap(apiTopics));
      }
      if (scheduleRes.status === 'fulfilled') {
        const sch = scheduleRes.value.data as any;
        const h = String(sch.hour ?? 9).padStart(2, '0');
        const m = String(sch.minute ?? 0).padStart(2, '0');
        setSendTime(`${h}:${m}`);
        const tz = typeof sch.timezone === 'string' && sch.timezone.trim() ? sch.timezone.trim() : 'Europe/Berlin';
        setSendTimezone(tz);
      }
      if (templatesRes.status === 'fulfilled') {
        const tpls = templatesRes.value.data as any[];
        if (tpls.length > 0) setAvailableTemplates(tpls);
      }
    }).finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [subscriberId]);

  /* ── Reload topics helper ── */
  const reloadTopics = useCallback(async () => {
    try {
      const res = await topicsApi.list(subscriberId);
      const apiTopics = res.data as any[];
      setTopics(apiTopics.map((t, i) => apiTopicToLocal(t, i)));
      setBuzzwordIdMap(buildBuzzwordIdMap(apiTopics));
    } catch { /* keep current state */ }
  }, [subscriberId]);

  /* ── Save settings ── */
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const [h, m] = sendTime.split(':').map(Number);
      await Promise.all([
        subscribersApi.update(subscriberId, { language }),
        subscriberScheduleApi.put(subscriberId, {
          hour: h,
          minute: m,
          is_enabled: true,
          timezone: sendTimezone,
        }),
        emailTemplatesApi.assignToSubscriber(subscriberId, templateId),
      ]);
      setSaveState('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => {
        // If the user edited a field during the badge window, go to dirty; else idle.
        setSaveState(pendingDirtyRef.current ? 'dirty' : 'idle');
        pendingDirtyRef.current = false;
        savedTimerRef.current = null;
      }, 1500);
      showSuccess(t('subscriberDetail.toastSettingsSaved'), 3500);
    } catch (e) {
      // Leave saveState as 'dirty' so the button stays for a retry.
      showError(scheduleApiError(e), 4500);
    }
    setSavingSettings(false);
  };

  const handleSendNewsletterNow = async () => {
    setSendingNewsletterNow(true);
    try {
      await subscriberScheduleApi.sendNow(subscriberId);
      showSuccess(t('subscriberDetail.toastNewsletterSent'), 4500);
    } catch (e) {
      showError(scheduleApiError(e), 5000);
    } finally {
      setSendingNewsletterNow(false);
    }
  };

  const handleAddTopic = async () => {
    const name = newTopicName.trim();
    if (!name) return;
    if (topics.some((t) => t.name.toLowerCase() === name.toLowerCase())) return;
    const color = TOPIC_COLORS[topics.length % TOPIC_COLORS.length];
    const colorToken = colorToToken(color, topics.length);
    // Optimistic update
    const tempId = nextTopicId;
    setTopics((prev) => [...prev, { id: tempId, name, buzzwords: [], color }]);
    setNextTopicId((n) => n + 1);
    setNewTopicName('');
    try {
      await topicsApi.create(subscriberId, { topic: name, color: colorToken });
      await reloadTopics();
    } catch { /* keep optimistic */ }
  };

  const handleDeleteTopic = async (topicId: number) => {
    // Optimistic update
    setTopics((prev) => prev.filter((t) => t.id !== topicId));
    try {
      await topicsApi.remove(subscriberId, topicId);
    } catch { await reloadTopics(); }
  };

  const handleAddBuzzword = async (topicId: number) => {
    const word = (newBuzzwords[topicId] ?? '').trim();
    if (!word) return;
    // Optimistic update
    setTopics((prev) =>
      prev.map((t) =>
        t.id === topicId
          ? t.buzzwords.includes(word) ? t : { ...t, buzzwords: [...t.buzzwords, word] }
          : t,
      ),
    );
    setNewBuzzwords((prev) => ({ ...prev, [topicId]: '' }));
    try {
      const res = await buzzwordsApi.add(subscriberId, topicId, { buzzword: word });
      const newId = (res.data as any)?.id;
      if (newId) {
        setBuzzwordIdMap((prev) => ({
          ...prev,
          [topicId]: { ...(prev[topicId] ?? {}), [word]: newId },
        }));
      }
    } catch { /* keep optimistic */ }
  };

  const handleRemoveBuzzword = async (topicId: number, word: string) => {
    // Optimistic update
    setTopics((prev) =>
      prev.map((t) =>
        t.id === topicId ? { ...t, buzzwords: t.buzzwords.filter((b) => b !== word) } : t,
      ),
    );
    const buzzwordId = buzzwordIdMap[topicId]?.[word];
    if (buzzwordId != null && buzzwordId !== -1) {
      try {
        await buzzwordsApi.remove(subscriberId, topicId, buzzwordId);
      } catch { await reloadTopics(); }
    }
  };

  /* ── Send now / regenerate / preview ── */
  const [sendingNow, setSendingNow] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSendNow = async () => {
    setSendingNow(true);
    try {
      await subscriberScheduleApi.sendNow(subscriberId);
    } catch { /* ignore — fire and forget */ }
    setSendingNow(false);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await aiGenerationApi.generateForSubscriber(subscriberId);
    } catch { /* ignore */ }
    setRegenerating(false);
  };

  const handleLoadPreview = async () => {
    setLoadingPreview(true);
    setPreviewOpen(true);
    try {
      const res = await emailNewsletterApi.preview(subscriberId, undefined);
      // Backend returns the rendered email under `html_content`; keep `html`/string
      // fallbacks so this still works if the response shape changes.
      const html =
        (res.data as any)?.html_content ??
        (res.data as any)?.html ??
        (typeof res.data === 'string' ? res.data : '');
      setPreviewHtml(html);
    } catch {
      setPreviewHtml(`<p>${t('subscriberDetail.previewUnavailable')}</p>`);
    } finally {
      setLoadingPreview(false);
    }
  };

  /* ── CSV import ── */
  const [csvImporting, setCsvImporting] = useState(false);

  const handleCsvImport = async (file: File) => {
    setCsvImporting(true);
    try {
      await topicsApi.importCsv(subscriberId, file);
      await reloadTopics();
    } catch { /* ignore */ }
    setCsvImporting(false);
  };

  const assignedTemplate = availableTemplates.find((t: any) => t.id === templateId);
  const defaultTemplate  = availableTemplates.find((t: any) => t.isDefault);

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-[rgb(224_231_255/0.55)]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (!subscriber) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-[rgb(224_231_255/0.55)]">
        <div className="text-center space-y-3">
          <p className="text-slate-500 text-sm">{t('subscriberDetail.notFound')}</p>
          <Button variant="outline" onClick={() => navigate('/subscribers')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('subscriberDetail.backToSubscribers')}
          </Button>
        </div>
      </div>
    );
  }

  const sendTimeLabel = formatSendTimeDisplay(sendTime);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[rgb(224_231_255/0.55)]  rounded-2xl">

      {/* ── Header ── */}
      <header className={`sticky top-0 z-20 shrink-0 border-b rounded-2xl border-white/40 ${LIQUID_GLASS_SOFT}`}>
        <div className="flex w-full min-w-0 items-center justify-between gap-4 px-6 py-4 rounded-2xl">
          <div className="flex min-w-0 items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/subscribers')}
              className="flex shrink-0 items-center gap-1.5 text-2xl font-bold tracking-tigh sm:text-3xl hawk-gradient-text text-slate-500 transition-colors hover:text-slate-800"
            >
              <ArrowLeft className="h-6 w-6" />
              {t('subscribers.title')}
            </button>
            <div className="h-5 w-px shrink-0 bg-slate-200" />
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-sm font-semibold text-white">
                {subscriber.mail.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{subscriber.mail}</p>
                <p className="text-xs text-slate-500">@{subscriber.username}</p>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              className={`gap-1.5 ${GLASS_FIELD}`}
              onClick={handleLoadPreview}
              disabled={loadingPreview}
            >
              {loadingPreview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
              {t('common.preview')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={`gap-1.5 ${GLASS_FIELD}`}
              onClick={handleRegenerate}
              disabled={regenerating}
            >
              {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {t('subscriberDetail.regenerate')}
            </Button>
            <Button
              size="sm"
              className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={handleSendNow}
              disabled={sendingNow}
            >
              {sendingNow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {t('subscriberDetail.sendNow')}
            </Button>
            <AnimatePresence mode="wait">
              {saveState === 'dirty' && (
                <motion.div key="unsaved" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                  <Button
                    size="sm"
                    className="gap-1.5 bg-blue-600 text-white hover:bg-blue-700"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                  >
                    {savingSettings ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    {t('subscriberDetail.saveChanges')}
                  </Button>
                </motion.div>
              )}
              {saveState === 'saved' && (
                <motion.span
                  key="saved"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs text-green-700"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> {t('subscriberDetail.allChangesSaved')}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      <div className="w-full min-w-0 space-y-6 px-6 py-6">

        {/* ── Settings cards ── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className={`rounded-2xl p-5 ${LIQUID_GLASS_CARD}`}>
            <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <Globe className="h-3.5 w-3.5" /> {t('common.language')}
            </div>
            <Select value={language} onValueChange={(v) => { setLanguage(v as LanguageCode); markDirty(); }}>
              <SelectTrigger className={`h-10 w-full gap-2 ${GLASS_FIELD} [&>svg:last-child]:ml-auto [&>svg:last-child]:text-slate-400`}>
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <LanguageFlagByCode code={language} className="h-4 w-4" />
                  <span className="truncate">{LANGUAGE_OPTIONS[language].countryLabel}</span>
                </span>
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_CODES.map((code) => (
                  <SelectItem key={code} value={code}>
                    <span className="flex items-center gap-2">
                      <LanguageFlagByCode code={code} className="h-4 w-4" />
                      <span>{LANGUAGE_OPTIONS[code].countryLabel}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-slate-500">
              {t('subscriberDetail.digestInLanguage', { language: languageDigestLabel(language) })}
            </p>
          </div>

          <div className={`rounded-2xl p-5 ${LIQUID_GLASS_CARD}`}>
            <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <Clock className="h-3.5 w-3.5" /> {t('subscriberDetail.dailySendTime')}
            </div>
            <input
              type="time"
              value={sendTime}
              onChange={(e) => { setSendTime(e.target.value); markDirty(); }}
              className={`hawk-native-datetime-input h-10 w-full px-3 text-sm font-medium text-slate-800 ${GLASS_FIELD}`}
            />
            <p className="mt-2 text-xs text-slate-500">
              {t('subscriberDetail.emailSentDailyAt', { time: sendTimeLabel })}
            </p>
          </div>

          <div className={`rounded-2xl p-5 ${LIQUID_GLASS_CARD}`}>
            <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <LayoutTemplate className="h-3.5 w-3.5" /> {t('subscriberDetail.emailTemplate')}
            </div>
            <Select
              value={templateId === null ? 'default' : String(templateId)}
              onValueChange={(v) => { setTemplateId(v === 'default' ? null : Number(v)); markDirty(); }}
            >
              <SelectTrigger className={`h-10 w-full ${GLASS_FIELD}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  {t('subscriberDetail.defaultOption', { name: defaultTemplate?.name ?? '—' })}
                </SelectItem>
                {availableTemplates.map((t: any) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    <span className="flex items-center gap-2">
                      {t.name}
                      {t.isDefault && <Star className="h-3 w-3 text-amber-500" />}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-slate-500">
              {assignedTemplate ? (
                t('subscriberDetail.usingTemplate', { name: assignedTemplate.name })
              ) : (
                t('subscriberDetail.usingDefault', { name: defaultTemplate?.name ?? '—' })
              )}
            </p>
          </div>
        </div>

        {/* ── Newsletter Preview ── */}
        {previewOpen && (
          <div className={`rounded-2xl ${LIQUID_GLASS_CARD}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-700">{t('subscriberDetail.newsletterPreview')}</p>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-2">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <iframe
                  srcDoc={previewHtml ?? ''}
                  title={t('subscriberDetail.previewIframeTitle')}
                  className="w-full rounded-lg border-0"
                  style={{ height: '480px' }}
                  sandbox="allow-same-origin"
                />
              )}
            </div>
          </div>
        )}

        {/* ── Topics & Buzzwords ── */}
        <div className={`rounded-2xl p-6 ${LIQUID_GLASS_PANEL}`}>
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <Tags className="h-4 w-4 text-purple-600" /> {t('subscriberDetail.topicsBuzzwords')}
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                {t('subscriberDetail.topicsDesc')}
              </p>
            </div>
            <Badge variant="secondary" className="shrink-0 text-xs">
              {topics.length === 1
                ? t('subscriberDetail.topicCount', { count: topics.length })
                : t('subscriberDetail.topicCountPlural', { count: topics.length })}
            </Badge>
          </div>

          {/* ── CSV Import ── */}
          <div className="mb-6">
            <label htmlFor="csv-upload" className="block cursor-pointer">
              <div
                className={`flex items-center gap-4 rounded-2xl border-2 border-dashed px-6 py-5 transition-colors ${
                  csvImporting
                    ? 'border-blue-300 bg-blue-50/50'
                    : 'border-slate-200/80 bg-white/40 hover:border-blue-300 hover:bg-white/60'
                }`}
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                    csvImporting ? 'bg-blue-100' : 'bg-slate-100'
                  }`}
                >
                  {csvImporting ? (
                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  ) : (
                    <Upload className="h-5 w-5 text-slate-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-medium ${csvImporting ? 'text-blue-600' : 'text-slate-700'}`}>
                    {csvImporting ? t('subscriberDetail.importing') : t('subscriberDetail.importCsv')}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    {t('subscriberDetail.importFormat', { format: t('subscriberDetail.importFormatToken') })}
                  </p>
                </div>
                {!csvImporting && (
                  <p className="hidden shrink-0 text-[10px] text-slate-400 sm:block">
                    {t('subscriberDetail.importDropHint')}
                  </p>
                )}
              </div>
            </label>
            <input
              id="csv-upload"
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="sr-only"
              disabled={csvImporting}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleCsvImport(file);
                e.target.value = '';
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

            {/* ── Topic cards ── */}
            <AnimatePresence initial={false}>
              {topics.map((topic) => {
                return (
                  <motion.div
                    key={topic.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className={`flex h-full flex-col rounded-2xl p-4 ${LIQUID_GLASS_CARD}`}>
                      <div className="mb-3 flex items-start justify-between gap-2">
                        <Badge className={`border px-2.5 py-0.5 text-xs font-semibold ${topic.color}`}>
                          {topic.name}
                        </Badge>
                        <button
                          type="button"
                          onClick={() => handleDeleteTopic(topic.id)}
                          className="shrink-0 rounded-lg p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                          title={t('subscriberDetail.deleteTopicTitle')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="mb-3 flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3 shrink-0 text-indigo-400" />
                        <span className="text-[11px] italic text-slate-500">{t('subscriberDetail.noArticlesMatched')}</span>
                      </div>
                      <div className="mb-3 flex flex-1 flex-wrap gap-1.5">
                        {topic.buzzwords.length === 0 ? (
                          <p className="text-xs italic text-slate-400">{t('subscriberDetail.noBuzzwordsYet')}</p>
                        ) : (
                          topic.buzzwords.map((word) => (
                            <motion.span
                              key={word}
                              layout
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/70 px-2.5 py-0.5 text-[11px] font-medium text-slate-700"
                            >
                              #{word}
                              <button
                                type="button"
                                onClick={() => handleRemoveBuzzword(topic.id, word)}
                                className="ml-0.5 text-slate-400 transition-colors hover:text-red-500"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </motion.span>
                          ))
                        )}
                      </div>
                      <div className="mt-auto flex gap-1.5">
                        <div className="relative min-w-0 flex-1">
                          <Hash className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                          <Input
                            placeholder={t('subscriberDetail.addBuzzwordPlaceholder')}
                            value={newBuzzwords[topic.id] ?? ''}
                            onChange={(e) => setNewBuzzwords((prev) => ({ ...prev, [topic.id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddBuzzword(topic.id); }}
                            className={`h-8 pl-7 text-xs ${GLASS_FIELD}`}
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className={`h-8 px-2.5 ${GLASS_FIELD}`}
                          onClick={() => handleAddBuzzword(topic.id)}
                          disabled={!(newBuzzwords[topic.id] ?? '').trim()}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* ── Add Topic card ── */}
            <motion.div layout>
              <div
                className="group flex h-full min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200/80 bg-white/30 p-6 transition-colors hover:border-purple-300 hover:bg-white/50"
                onClick={() => { document.getElementById('new-topic-input')?.focus(); }}
              >
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-slate-200 bg-white/60 transition-colors group-hover:border-purple-300 group-hover:bg-purple-50">
                  <Plus className="h-5 w-5 text-slate-400 transition-colors group-hover:text-purple-600" />
                </div>
                <p className="mb-3 text-xs font-medium text-slate-500 transition-colors group-hover:text-purple-600">
                  {t('subscriberDetail.addNewTopic')}
                </p>
                <div className="flex w-full gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <Input
                    id="new-topic-input"
                    placeholder={t('subscriberDetail.topicNamePlaceholder')}
                    value={newTopicName}
                    onChange={(e) => setNewTopicName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddTopic(); }}
                    className={`h-8 text-xs ${GLASS_FIELD}`}
                  />
                  <Button
                    size="sm"
                    className="h-8 bg-purple-600 px-2.5 text-white hover:bg-purple-700"
                    onClick={() => handleAddTopic()}
                    disabled={!newTopicName.trim()}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Summary */}
          {topics.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-xs text-indigo-700"
            >
              <Sparkles className="h-3.5 w-3.5 shrink-0" />
              <span>
                {t('subscriberDetail.aiSummary', {
                  topicCount: topics.length === 1
                    ? t('subscriberDetail.aiSummaryTopicsOne', { count: topics.length })
                    : t('subscriberDetail.aiSummaryTopicsMany', { count: topics.length }),
                  email: subscriber.mail,
                  time: sendTimeLabel,
                  timezone: sendTimezone,
                })}
              </span>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
